import {TableColumnData} from "../../../types/common";
import {OrderListVO, RecallStatus} from "../../../types/orders";
import {convertCurrencyUnit, numberWithCommas} from "../../../util/etcUtil";
import React from "react";
import {LinkStyle} from "../../../components/styled/LinkStyle";
import {OrderItemColumn} from "../component/OrderItemColumn";
import {OrderStatusStyle} from "../component/OrderStatusStyle";
import {useNationality, useNewWindow, useTimeConverter} from "../../../hooks/hooks";
import {useOrderButtons} from "./useOrderButtons";
import {ActionButtonWrapper} from "../../../layout/content/ActionButtonWrapper";
import {useTranslation} from "react-i18next";
import {useOrdersType} from "../../../types/hooks/useOrdersType";
import {usePaymentType} from "../../../types/hooks/usePaymentType";
import {useProductType} from "../../../types/hooks/useProductType";
import style from "../order.module.scss";
import {OrderCodeStatusField} from "../component/OrderCodeStatusField";

interface Args {
  onRefresh: () => void;
}
export default function useOrderColumns({ onRefresh }: Args): TableColumnData<OrderListVO>[] {
  const { t } = useTranslation("order");
  const { openCustomerPopup, openOrderDetailPopup } = useNewWindow();
  const { MemoButton, DeleteButton } = useOrderButtons({ onRefresh });
  const { timeConverter } = useTimeConverter();
  const { toStringFromOrderStatus, toStringFromRecallStatus } = useOrdersType();
  const { toStringFromPaymentWay } = usePaymentType();
  const { toStringFromDisplayPosition } = useProductType();
  const { isKorean } = useNationality();

  return [
    {
      accessor: "idx",
      header: t("columns.orderedAt"),
      getNode: (o: OrderListVO) => (
        <div
          onClick={(e) => {
            e.preventDefault();
            openOrderDetailPopup(o.idx);
          }}
        >
          <LinkStyle>{timeConverter.convertToLocalTime(o.createAt)}</LinkStyle>
          <br />
          {o.paidAt && <span>({timeConverter.convertToLocalTime(o.paidAt)})</span>}
        </div>
      ),
      sortable: true,
    },
    {
      accessor: "buyerName",
      header: t("columns.buyerName"),
      sortable: false,
      getNode: (o: OrderListVO) => (
        <LinkStyle
          onClick={(e) => {
            e.preventDefault();
            openCustomerPopup(o.buyerIdx, o.customerType);
          }}
        >
          {o.buyerName}
        </LinkStyle>
      ),
      width: 104,
    },
    {
      accessor: "displayPosition",
      header: t("columns.displayPosition"),
      sortable: false,
      getNode: (o: OrderListVO) => <span>{toStringFromDisplayPosition(o.displayPosition)}</span>,
    },
    {
      accessor: "orderTitle",
      header: t("columns.productName"),
      sortable: false,
      getNode: (o: OrderListVO) => <OrderItemColumn orders={o} />,
      width: 370,
    },
    {
      accessor: "regularPriceAmount",
      header: t("columns.totalPrice"),
      sortable: false,
      getNode: (o: OrderListVO) => <>
        {numberWithCommas(o.regularPriceAmount)}{convertCurrencyUnit(o.paymentWay, t)}
      </>,
      isRightAlign: true,
      className: style.rightPadding12,
    },
    {
      accessor: "payAmount",
      header: t("columns.actualAmount"),
      sortable: false,
      getNode: (o: OrderListVO) =>
      <>
        {numberWithCommas(o.payAmount)}{convertCurrencyUnit(o.paymentWay, t)}
      </>,
      isRightAlign: true,
      className: style.rightPadding12,
    },
    {
      accessor: "paymentWay",
      header: t("columns.paymentWay"),
      sortable: false,
      getNode: (o: OrderListVO) => <span>{toStringFromPaymentWay(o.paymentWay)}</span>,
    },
    {
      accessor: "status",
      header: t("columns.status"),
      sortable: false,
      getNode: (o: OrderListVO) => (
            o.recallStatus === RecallStatus.NONE ? (
                <OrderStatusStyle orderStatus={o.status}>{toStringFromOrderStatus(o.status)}</OrderStatusStyle>
            ) : (
                <OrderStatusStyle
                    orderStatus={o.status}>
                  {toStringFromRecallStatus(o.recallStatus, o.status)}
                </OrderStatusStyle>
            )
      ),
    },
    {
      accessor: "affiliateCode",
      header: "코드",
      sortable: false,
      getNode: (o: OrderListVO) => <OrderCodeStatusField orderList={o} />,
      hideThisColumn: !isKorean,
    },
    {
      accessor: "idx",
      header: t("columns.additionalFunctions"),
      getNode: (o: OrderListVO) => (
        <ActionButtonWrapper>
          <>
            <MemoButton orderIdx={o.idx} hasMemo={o.hasMemo} />
            <DeleteButton orderIdx={o.idx} orderStatus={o.status} />
          </>
        </ActionButtonWrapper>
      ),
      sortable: false,
    },
  ];
}
